import React from 'react';
import {
  Home, Workflow, FolderOpen, Tag, Users,
  UsersRound, Webhook, Cpu, CreditCard, Trash2,
  Video,
  LayoutGrid,
} from 'lucide-react';
import InstitutionDashboard from '@/components/institution/InstitutionDashboard';
import GroupManagement from '@/components/institution/GroupManagement';
import WebhookConfig from '@/components/institution/WebhookConfig';
import InstitutionCategoriesPanel from '@/pages/institution/InstitutionCategoriesPanel';
import InstitutionMcpPanel from '@/pages/institution/InstitutionMcpPanel';
import InstitutionBillingPanel from '@/pages/institution/InstitutionBillingPanel';
import InstitutionTrashPanel from '@/pages/institution/InstitutionTrashPanel';
import InstitutionWebinarSettingsPanel from '@/pages/institution/InstitutionWebinarSettingsPanel';
import InstitutionBiPanel from '@/pages/institution/InstitutionBiPanel';
import { FormateurFlowsTabContent } from '@/components/dashboard/FormateurFlowsTabContent';
import { FormateurSupportsTabContent } from '@/components/dashboard/FormateurSupportsTabContent';
import { FormateurLearnersTabContent } from '@/components/dashboard/FormateurLearnersTabContent';
import { INSTITUTION_MENU_FLAGS, type InstitutionMenuFlagKey } from '@/lib/institution-menu-flags';
import { isLyrraOne } from '@/lib/platform';

export type InstitutionNavSectionId = 'general' | 'rh_tools' | 'content' | 'people' | 'portal' | 'integrations' | 'subscription';

export const INSTITUTION_NAV_SECTION_ORDER: readonly InstitutionNavSectionId[] = [
  'general', 'content', 'people', 'rh_tools', 'portal', 'integrations', 'subscription',
] as const;

export const INSTITUTION_SECTION_LABEL_KEYS: Record<InstitutionNavSectionId, string> = {
  general: 'institution.nav.section.general',
  rh_tools: 'institution.nav.section.rhTools',
  content: 'institution.nav.section.content',
  people: 'institution.nav.section.people',
  portal: 'institution.nav.section.portal',
  integrations: 'institution.nav.section.integrations',
  subscription: 'institution.nav.section.subscription',
};

export const institutionTabDefs = [
  { id: 'home', section: 'general' as const, labelKey: 'institution.tabs.home', icon: Home, menuSortRank: 0 },
  { id: 'webinars', section: 'content' as const, labelKey: 'institution.tabs.webinars', icon: Video, menuFlag: INSTITUTION_MENU_FLAGS.tab_webinar_settings, menuSortRank: 2 },
  { id: 'flows', section: 'content' as const, labelKey: 'institution.tabs.flows', icon: Workflow, menuFlag: INSTITUTION_MENU_FLAGS.tab_flows, menuSortRank: 1 },
  { id: 'trash', section: 'content' as const, labelKey: 'institution.tabs.trash', icon: Trash2, menuFlag: INSTITUTION_MENU_FLAGS.tab_trash, menuSortRank: 8 },
  { id: 'supports', section: 'content' as const, labelKey: 'institution.tabs.supports', icon: FolderOpen, menuFlag: INSTITUTION_MENU_FLAGS.tab_supports, menuSortRank: 4 },
  { id: 'categories', section: 'content' as const, labelKey: 'institution.tabs.categories', icon: Tag, menuFlag: INSTITUTION_MENU_FLAGS.tab_categories, menuSortRank: 5 },
  { id: 'learners', section: 'people' as const, labelKey: 'institution.tabs.learners', icon: Users, menuFlag: INSTITUTION_MENU_FLAGS.tab_learners },
  { id: 'bi-studio', section: 'people' as const, labelKey: 'institution.tabs.biStudio', icon: LayoutGrid, menuFlag: INSTITUTION_MENU_FLAGS.tab_bi_studio },
  { id: 'groups', section: 'people' as const, labelKey: 'institution.tabs.groups', icon: UsersRound, menuFlag: INSTITUTION_MENU_FLAGS.tab_groups },
  { id: 'webhooks', section: 'integrations' as const, labelKey: 'institution.tabs.webhooks', icon: Webhook, menuFlag: INSTITUTION_MENU_FLAGS.tab_webhooks },
  { id: 'mcp', section: 'integrations' as const, labelKey: 'institution.tabs.mcp', icon: Cpu, menuFlag: INSTITUTION_MENU_FLAGS.tab_mcp },
  { id: 'billing', section: 'subscription' as const, labelKey: 'institution.tabs.billing', icon: CreditCard, menuFlag: INSTITUTION_MENU_FLAGS.tab_billing },
] as const;

export type InstitutionTabId = (typeof institutionTabDefs)[number]['id'];

/** LyrraOne : l’onglet billing affiche les ventes formateur, pas l’abonnement plateforme. */
export function institutionTabLabelKey(tabId: InstitutionTabId): string {
  if (isLyrraOne() && tabId === 'billing') return 'institution.tabs.sales';
  const def = institutionTabDefs.find((d) => d.id === tabId);
  return def?.labelKey ?? tabId;
}

export const institutionTabContent: Record<InstitutionTabId, React.ReactNode> = {
  home: <InstitutionDashboard />,
  flows: <FormateurFlowsTabContent />,
  trash: <InstitutionTrashPanel />,
  supports: <FormateurSupportsTabContent />,
  categories: <InstitutionCategoriesPanel />,
  webinars: <InstitutionWebinarSettingsPanel />,
  learners: <FormateurLearnersTabContent />,
  'bi-studio': <InstitutionBiPanel />,
  groups: <GroupManagement />,
  webhooks: <WebhookConfig />,
  mcp: <InstitutionMcpPanel />,
  billing: <InstitutionBillingPanel />,
};

/** Entrées sidebar « Formation & RH » — vide depuis retrait des modules RH entreprise (LyrraOne). */
export const INSTITUTION_RH_SIDEBAR_ENTRIES: readonly [] = [];

export function isValidInstitutionTab(tab: string | null): tab is InstitutionTabId {
  if (!tab) return false;
  return institutionTabDefs.some((d) => d.id === tab);
}

export function institutionTabMenuFlagKey(tab: InstitutionTabId, _rh: string | null): InstitutionMenuFlagKey | null {
  const def = institutionTabDefs.find((d) => d.id === tab);
  return def?.menuFlag ?? null;
}

export function formateurDashboardTabPath(tab: InstitutionTabId): string {
  if (tab === 'home') return '/dashboard';
  const params = new URLSearchParams();
  params.set('tab', tab);
  return `/dashboard?${params.toString()}`;
}

/** LyrraOne : onglets « paramétrage » sous `/settings`, le reste sous `/dashboard`. */
export type InstitutionTabSurface = 'dashboard' | 'settings';

const LYRRAONE_SETTINGS_TABS = new Set<InstitutionTabId>([
  'categories',
  'webinars',
  'groups',
  'webhooks',
  'mcp',
  'bi-studio',
  'trash',
]);

export function institutionTabSurface(tab: InstitutionTabId): InstitutionTabSurface {
  if (!isLyrraOne()) return 'dashboard';
  return LYRRAONE_SETTINGS_TABS.has(tab) ? 'settings' : 'dashboard';
}

export function isInstitutionSettingsTab(tab: string | null): tab is InstitutionTabId {
  return isValidInstitutionTab(tab) && institutionTabSurface(tab) === 'settings';
}

export function isInstitutionDashboardTab(tab: string | null): tab is InstitutionTabId {
  return isValidInstitutionTab(tab) && institutionTabSurface(tab) === 'dashboard';
}

export function formateurSettingsTabPath(tab: InstitutionTabId): string {
  if (tab === 'home') return '/settings';
  const params = new URLSearchParams();
  params.set('tab', tab);
  return `/settings?${params.toString()}`;
}

export function formateurTabPath(tab: InstitutionTabId): string {
  return institutionTabSurface(tab) === 'settings'
    ? formateurSettingsTabPath(tab)
    : formateurDashboardTabPath(tab);
}

/** Institution UI rendue sous `/dashboard` (onglets ou fiches apprenants). */
export function isFormateurDashboardInstitutionPath(pathname: string): boolean {
  return pathname === '/dashboard' || pathname.startsWith('/dashboard/learners');
}

export function institutionLearnerDetailPath(participantId: string, pathname: string): string {
  return isFormateurDashboardInstitutionPath(pathname)
    ? `/dashboard/learners/${participantId}`
    : `/institution/learners/${participantId}`;
}

export function institutionLearnerBilanPath(participantId: string, pathname: string): string {
  return isFormateurDashboardInstitutionPath(pathname)
    ? `/dashboard/learners/${participantId}/bilan`
    : `/institution/learners/${participantId}/bilan`;
}
